import { useEffect, useState } from 'react';
import {
  Business as BusinessIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { type Tenant, getAllTenants } from '@core/api/tenant';
import { useBrand } from '@core/contexts/BrandContext';
import { useMenu } from '@core/hooks/useMenu';

export interface TenantSwitchMenuItemProps {
  tenant: Tenant | null;
  onSelect: (tenant: Tenant | null) => void;
  onDone?: () => void;
}

const TenantSwitchMenuItem: React.FC<TenantSwitchMenuItemProps> = ({
  tenant,
  onSelect,
  onDone,
}) => {
  const { name: brandName } = useBrand();
  const { anchorEl, isOpen, openMenu, closeMenu } = useMenu();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAllTenants()
      .then(setTenants)
      .catch(() => setTenants([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (tenant: Tenant | null) => {
    onSelect(tenant);
    closeMenu();
    onDone?.();
    window.location.reload();
  };

  return (
    <>
      <MenuItem onClick={openMenu} disabled={loading || tenants.length === 0}>
        <ListItemIcon>
          <BusinessIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Switch tenant</ListItemText>
      </MenuItem>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        PaperProps={{
          sx: {
            mt: -1,
            minWidth: 225,
            maxHeight: 300,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem selected={tenant === null} onClick={() => handleSelect(null)}>
          <ListItemIcon>
            {tenant === null ? <CheckIcon fontSize="small" /> : null}
          </ListItemIcon>
          <ListItemText>{brandName}</ListItemText>
        </MenuItem>
        {tenants.map((item) => (
          <MenuItem
            key={item.id}
            selected={tenant?.id === item.id}
            onClick={() => handleSelect(item)}
          >
            <ListItemIcon>
              {tenant?.id === item.id ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{item.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TenantSwitchMenuItem;
